// =====================================================================
// LANDING PÚBLICA do portal do estudante (rota "/").
//
// Herda a ideia da home do UEN (hero → prova social → como funciona →
// benefícios → validação gov.br → dúvidas → rodapé), mas reconstruída:
//   - 100% em cima dos tokens de tema (claro/escuro), nada de cor fixa;
//   - a carteirinha do hero é DESENHADA em CSS, não é imagem — não
//     depende de asset que ninguém aprovou ainda e nunca fica borrada;
//   - nav fixa com âncoras, barra de progresso e âncora ativa (scroll spy);
//   - reveal por IntersectionObserver, desligado em prefers-reduced-motion.
//
// >>> ONDE ENTRAM AS IMAGENS: veja o bloco IMAGENS logo abaixo. Enquanto
//     um slot estiver `null`, a seção mostra uma moldura marcada com a
//     medida sugerida — o layout não quebra e nada some da página.
//
// Não chama API nenhuma: é estática de propósito (é a porta de entrada,
// tem que abrir mesmo com o backend fora do ar).
// =====================================================================
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppleStoreButton, GooglePlayButton } from '../../components/StoreButtons';
import {
    ArrowRight, ArrowUp, BadgeCheck, CalendarCheck, Clapperboard, CreditCard,
    Drama, FileCheck2, Gift, Headset, Image as ImageIcon, Menu, Music4, QrCode,
    Send, ShieldCheck, Smartphone, Ticket, Trophy, Truck, UserPlus, X,
} from 'lucide-react';

import Brand from '../../components/Brand';
import ThemeToggle from '../../components/ThemeToggle';
import { isAuthenticated } from '../../auth/storage';
import { abrirWhatsappSuporte, GOV_VALIDAR_URL } from '../../config';
import logoUeb from '../../assets/ueb.png';
import logoIcp from '../../assets/logo_icp.png';
import logoIti from '../../assets/logo_iti.png';
import fotoCinema from '../../assets/landing/cinema.jpg';
import fotoEstudante from '../../assets/landing/estudante.png';
import './landing.css';

// =====================================================================
// IMAGENS DA LANDING
// ---------------------------------------------------------------------
// 1. jogue os arquivos em  src/assets/landing/
// 2. importe em cima:      import vitrineFrente from '../../assets/landing/carteira-frente.png';
// 3. troque o `null` pelo import.
// Tudo que continuar `null` vira uma moldura vazia com a medida sugerida.
// =====================================================================
const IMAGENS = {
    // vitrine em abas (a carteira por dentro) — 1200x750 (16/10)
    vitrine: { frente: null, verso: null, app: null },
    // faixa larga entre benefícios e validação — 1920x720 (21/9)
    banner: fotoCinema,
};

const ACEITA_EM = [
    { icon: <Clapperboard size={20} />, label: 'Cinema' },
    { icon: <Music4 size={20} />, label: 'Shows' },
    { icon: <Drama size={20} />, label: 'Teatro' },
    { icon: <Trophy size={20} />, label: 'Jogos' },
    { icon: <Ticket size={20} />, label: 'Eventos' },
];

const PASSOS = [
    {
        icon: <UserPlus size={22} />,
        titulo: 'Crie sua conta',
        texto: 'Email e senha. Leva menos de um minuto e você já entra no portal.',
    },
    {
        icon: <FileCheck2 size={22} />,
        titulo: 'Envie seus dados',
        texto: 'Foto, curso, instituição e o comprovante de matrícula. Tudo pelo celular.',
    },
    {
        icon: <CreditCard size={22} />,
        titulo: 'Pague como preferir',
        texto: 'Pix na hora ou cartão de crédito, com o pagamento processado com segurança.',
    },
    {
        icon: <Smartphone size={22} />,
        titulo: 'Use sua carteira',
        texto: 'A versão digital fica disponível no portal. A física, se você quiser, vai pelos Correios.',
    },
];

const BENEFICIOS = [
    {
        icon: <Ticket size={26} />,
        titulo: 'Meia-entrada em todo o Brasil',
        texto: 'Seu direito garantido pela Lei 12.933/13 em cinemas, shows, teatros e eventos esportivos.',
    },
    {
        icon: <ShieldCheck size={26} />,
        titulo: 'Documento assinado digitalmente',
        texto: 'Cada carteira gera um PDF com assinatura ICP-Brasil, conferível no site do governo.',
    },
    {
        icon: <QrCode size={26} />,
        titulo: 'Validação instantânea por QR',
        texto: 'Quem confere aponta a câmera e vê na hora se a carteira é legítima e está no prazo.',
    },
    {
        icon: <Truck size={26} />,
        titulo: 'Via física na sua casa',
        texto: 'Quer o cartão impresso? A gente envia pelos Correios com código de rastreio no portal.',
    },
    {
        icon: <Gift size={26} />,
        titulo: 'Descontos além da meia',
        texto: 'Sua identificação de estudante abre portas em parceiros, cursos e serviços.',
    },
    {
        icon: <Headset size={26} />,
        titulo: 'Suporte de gente de verdade',
        texto: 'Time no WhatsApp pra destravar seu pedido — sem robô, sem fila de espera.',
    },
];

// A régua de números conta do zero até `alvo` quando entra na tela.
// Entrada sem `alvo` (só `texto`) fica estática.
const NUMEROS = [
    { alvo: 46, prefixo: '+', sufixo: ' mil', label: 'carteiras já emitidas' },
    { alvo: 27, label: 'estados atendidos' },
    { alvo: 50, sufixo: '%', label: 'de desconto garantido' },
    { texto: 'ICP', label: 'assinatura Brasil' },
];

const DUVIDAS = [
    {
        pergunta: 'A carteira da UEB vale em todo o Brasil?',
        resposta:
            'Sim. A CIE (Carteira de Identificação Estudantil) emitida pela UEB é válida em todo o território nacional e assegura o direito à meia-entrada previsto na Lei 12.933/13.',
    },
    {
        pergunta: 'Quanto tempo demora pra minha carteira ficar pronta?',
        resposta:
            'Depois do pagamento confirmado e dos documentos enviados, a análise é feita pela nossa equipe e a versão digital é liberada no portal. Você acompanha o status por lá, sem precisar ligar pra ninguém.',
    },
    {
        pergunta: 'Como alguém confere que a minha carteira é verdadeira?',
        resposta:
            'De duas formas: pelo QR Code impresso na carteira, que abre a página pública de validação com seus dados acadêmicos; e pelo PDF assinado com certificado ICP-Brasil, que pode ser conferido no validador oficial do governo (validar.iti.gov.br).',
    },
    {
        pergunta: 'Preciso pedir a via física?',
        resposta:
            'Não. A carteira digital já vale sozinha. A via física é opcional — se você pedir, ela é enviada pelos Correios para o endereço cadastrado e o código de rastreio aparece no seu painel.',
    },
    {
        pergunta: 'Quais formas de pagamento vocês aceitam?',
        resposta:
            'Pix, com liberação imediata assim que o pagamento é identificado, e cartão de crédito. Os dados do cartão são processados pelo provedor de pagamento e não ficam armazenados conosco.',
    },
];

const ANO = 2026;

const querMenosMovimento = () =>
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* --------------------------------------------------------------------
   Reveal na entrada da seção. Um observer só, compartilhado por todos os
   elementos com [data-reveal]. Se o usuário pediu menos animação (ou o
   navegador não tem IntersectionObserver), tudo já nasce visível.
   -------------------------------------------------------------------- */
function useRevealOnScroll() {
    useEffect(() => {
        const alvos = Array.from(document.querySelectorAll('[data-reveal]'));
        if (querMenosMovimento() || typeof IntersectionObserver === 'undefined') {
            alvos.forEach((el) => el.classList.add('is-in'));
            return;
        }
        const obs = new IntersectionObserver(
            (entradas) => {
                entradas.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add('is-in');
                        obs.unobserve(e.target);
                    }
                });
            },
            { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
        );
        alvos.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);
}

/* Marca qual seção está na tela, pra acender o link certo na nav. */
function useSecaoAtiva(ids) {
    const [ativa, setAtiva] = useState('');
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return;
        const secoes = ids.map((id) => document.getElementById(id)).filter(Boolean);
        const obs = new IntersectionObserver(
            (entradas) => {
                const visivel = entradas
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
                if (visivel) setAtiva(visivel.target.id);
            },
            { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );
        secoes.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
    }, [ids]);
    return ativa;
}

/* --------------------------------------------------------------------
   Moldura de imagem. Com `src`, mostra a foto; sem `src`, mostra o
   espaço reservado com a medida sugerida — assim dá pra publicar antes
   de ter o material e trocar depois sem mexer no layout.
   -------------------------------------------------------------------- */
function Figura({ src, alt, medida, proporcao = '16 / 10', className = '', children }) {
    return (
        <div
            className={`lp-fig ${src ? '' : 'lp-fig--vazia'} ${className}`}
            style={{ '--prop': proporcao }}
        >
            {src ? (
                <img src={src} alt={alt} loading="lazy" />
            ) : (
                <div className="lp-fig-placeholder">
                    <ImageIcon size={26} />
                    <strong>{alt}</strong>
                    {medida && <small>{medida}</small>}
                </div>
            )}
            {children}
        </div>
    );
}

/* Número que conta do zero ao alvo quando entra na viewport. */
function Contador({ alvo, prefixo = '', sufixo = '' }) {
    const ref = useRef(null);
    const [valor, setValor] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (querMenosMovimento() || typeof IntersectionObserver === 'undefined') {
            setValor(alvo);
            return;
        }
        let raf = 0;
        const obs = new IntersectionObserver(
            (entradas) => {
                if (!entradas[0].isIntersecting) return;
                obs.disconnect();
                const inicio = performance.now();
                const dur = 1100;
                const passo = (agora) => {
                    const t = Math.min(1, (agora - inicio) / dur);
                    // easeOutCubic: começa rápido e assenta no número final
                    const e = 1 - Math.pow(1 - t, 3);
                    setValor(Math.round(alvo * e));
                    if (t < 1) raf = requestAnimationFrame(passo);
                };
                raf = requestAnimationFrame(passo);
            },
            { threshold: 0.4 }
        );
        obs.observe(el);
        return () => { obs.disconnect(); cancelAnimationFrame(raf); };
    }, [alvo]);

    return <span ref={ref}>{prefixo}{valor}{sufixo}</span>;
}

/* Cartão da carteirinha desenhado em CSS (hero). Segue o layout real:
   faixa da marca em cima, foto, dados, QR e validade. O cartão acompanha
   o mouse — em toque/reduced-motion fica na inclinação padrão. */
function CarteirinhaMock() {
    const ref = useRef(null);

    const mover = (ev) => {
        const el = ref.current;
        if (!el || querMenosMovimento()) return;
        const r = el.getBoundingClientRect();
        const x = (ev.clientX - r.left) / r.width - 0.5;
        const y = (ev.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--ry', `${x * 18 - 8}deg`);
        el.style.setProperty('--rx', `${-y * 14 + 4}deg`);
    };
    const sair = () => {
        const el = ref.current;
        if (!el) return;
        el.style.removeProperty('--ry');
        el.style.removeProperty('--rx');
    };

    return (
        <div
            ref={ref}
            className="lp-card3d"
            aria-hidden="true"
            onMouseMove={mover}
            onMouseLeave={sair}
        >
            <div className="lp-card3d-glow" />
            <div className="lp-card">
                <div className="lp-card-brilho" />
                <div className="lp-card-top">
                    <img src={logoUeb} alt="" className="lp-card-logo" />
                    <div className="lp-card-top-text">
                        <strong>UEB</strong>
                        <span>Carteira de Identificação Estudantil</span>
                    </div>
                </div>

                <div className="lp-card-body">
                    <div className="lp-card-foto" />
                    <div className="lp-card-dados">
                        <div className="lp-card-linha">
                            <small>Nome</small>
                            <span className="lp-skeleton w-90" />
                        </div>
                        <div className="lp-card-linha">
                            <small>Curso</small>
                            <span className="lp-skeleton w-70" />
                        </div>
                        <div className="lp-card-linha">
                            <small>Instituição</small>
                            <span className="lp-skeleton w-80" />
                        </div>
                    </div>
                </div>

                <div className="lp-card-foot">
                    <div className="lp-card-qr"><QrCode size={38} /></div>
                    <div className="lp-card-validade">
                        <small>Validade</small>
                        <strong>31/03/{ANO + 1}</strong>
                    </div>
                    <div className="lp-card-selo"><BadgeCheck size={16} /> Válida</div>
                </div>
            </div>

            {/* Selos flutuantes ao redor do cartão */}
            <div className="lp-float lp-float--a"><ShieldCheck size={16} /> Assinada ICP-Brasil</div>
            <div className="lp-float lp-float--b"><CalendarCheck size={16} /> Meia-entrada garantida</div>
        </div>
    );
}

export default function Landing() {
    const navigate = useNavigate();
    const logado = isAuthenticated();
    const [scrolled, setScrolled] = useState(false);
    const [progresso, setProgresso] = useState(0);
    const [aoTopo, setAoTopo] = useState(false);
    const [menuAberto, setMenuAberto] = useState(false);
    const [faqAberta, setFaqAberta] = useState(0);

    useRevealOnScroll();

    const links = [
        { href: '#como-funciona', id: 'como-funciona', label: 'Como funciona' },
        { href: '#beneficios', id: 'beneficios', label: 'Benefícios' },
        { href: '#validacao', id: 'validacao', label: 'Validação' },
        { href: '#duvidas', id: 'duvidas', label: 'Dúvidas' },
    ];
    const idsRef = useRef(links.map((l) => l.id));
    const secaoAtiva = useSecaoAtiva(idsRef.current);

    // Um listener de scroll só, em rAF: estado da nav + barra de progresso
    // + botão de voltar ao topo.
    useEffect(() => {
        let tick = false;
        const medir = () => {
            tick = false;
            const y = window.scrollY;
            const total = document.documentElement.scrollHeight - window.innerHeight;
            setScrolled(y > 12);
            setAoTopo(y > 700);
            setProgresso(total > 0 ? Math.min(1, y / total) : 0);
        };
        const onScroll = () => {
            if (tick) return;
            tick = true;
            requestAnimationFrame(medir);
        };
        medir();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    // Trava o corpo enquanto o menu mobile está aberto (senão a página
    // rola por baixo do painel).
    useEffect(() => {
        document.body.style.overflow = menuAberto ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuAberto]);

    const irParaPortal = () => navigate(logado ? '/' : '/registrar');
    const ctaPrincipal = logado ? 'Ir para meu portal' : 'Quero minha carteira';

    return (
        <div className="lp">
            {/* barra de leitura no topo */}
            <div className="lp-progresso" aria-hidden="true">
                <span style={{ transform: `scaleX(${progresso})` }} />
            </div>

            {/* ---------------- NAV ---------------- */}
            <header className={`lp-nav ${scrolled ? 'is-scrolled' : ''}`}>
                <div className="lp-nav-inner">
                    <Link to="/" className="lp-nav-brand" aria-label="UEB — início">
                        <Brand logoOnly />
                    </Link>

                    <nav className="lp-nav-links" aria-label="Seções">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                className={secaoAtiva === l.id ? 'is-ativa' : ''}
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="lp-nav-actions">
                        <ThemeToggle />
                        {logado ? (
                            <Link className="pbtn pbtn--primary" to="/">Meu portal</Link>
                        ) : (
                            <>
                                <Link className="pbtn pbtn--ghost lp-only-desktop" to="/login">Entrar</Link>
                                <Link className="pbtn pbtn--primary" to="/registrar">Criar conta</Link>
                            </>
                        )}
                        <button
                            type="button"
                            className="lp-burger"
                            onClick={() => setMenuAberto(true)}
                            aria-label="Abrir menu"
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            {/* -------- MENU MOBILE -------- */}
            {menuAberto && (
                <div className="lp-drawer" role="dialog" aria-modal="true" aria-label="Menu">
                    <div className="lp-drawer-top">
                        <Brand />
                        <button type="button" className="lp-burger" onClick={() => setMenuAberto(false)} aria-label="Fechar menu">
                            <X size={20} />
                        </button>
                    </div>
                    <nav className="lp-drawer-links">
                        {links.map((l) => (
                            <a key={l.href} href={l.href} onClick={() => setMenuAberto(false)}>{l.label}</a>
                        ))}
                    </nav>
                    <div className="lp-drawer-actions">
                        {!logado && <Link className="pbtn pbtn--ghost pbtn--block" to="/login">Entrar</Link>}
                        <Link className="pbtn pbtn--primary pbtn--block" to={logado ? '/' : '/registrar'}>
                            {ctaPrincipal}
                        </Link>
                    </div>
                </div>
            )}

            {/* ---------------- HERO ---------------- */}
            <section className="lp-hero">
                <div className="lp-hero-bg" aria-hidden="true">
                    <span className="lp-orb lp-orb--1" />
                    <span className="lp-orb lp-orb--2" />
                    <span className="lp-hero-malha" />
                </div>
                <div className="lp-wrap lp-hero-grid">
                    <div className="lp-hero-text" data-reveal>
                        <span className="lp-eyebrow">
                            <ShieldCheck size={14} /> Lei 12.933/13 · Assinatura ICP-Brasil
                        </span>

                        <h1>
                            Sua <span className="lp-hl">carteira de estudante</span> legítima,
                            pronta em tempo recorde.
                        </h1>

                        <p className="lp-hero-sub">
                            Emita, acompanhe e valide sua CIE da UEB em um só lugar. Digital na hora,
                            física entregue em casa e um documento assinado que qualquer um pode conferir.
                        </p>

                        <div className="lp-hero-cta">
                            <button type="button" className="pbtn pbtn--primary pbtn--lg" onClick={irParaPortal}>
                                {ctaPrincipal} <ArrowRight size={18} />
                            </button>
                            <a className="pbtn pbtn--ghost pbtn--lg" href="#como-funciona">Ver como funciona</a>
                        </div>

                        <ul className="lp-hero-trust">
                            <li><BadgeCheck size={16} /> Aceita em todo o Brasil</li>
                            <li><CreditCard size={16} /> Pix ou cartão</li>
                            <li><Truck size={16} /> Entrega pelos Correios</li>
                        </ul>
                    </div>

                    <div className="lp-hero-visual" data-reveal>
                        <CarteirinhaMock />
                    </div>
                </div>

                {/* faixa "aceita em" ancorada no fim do hero */}
                <div className="lp-wrap">
                    <div className="lp-aceita" data-reveal>
                        <span className="lp-aceita-label">Aceita em</span>
                        <div className="lp-aceita-itens">
                            {ACEITA_EM.map((a) => (
                                <span className="lp-aceita-chip" key={a.label}>{a.icon}{a.label}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- APP ---------------- */}
            <section className="lp-wrap">
                <div className="lp-tele" data-reveal>
                    <div className="lp-tele-txt">
                        <span className="lp-tele-tag"><Smartphone size={15} /> App do estudante</span>
                        <h2>Sua carteirinha UEB <span className="lp-tele-dest">no seu bolso</span>, a qualquer hora</h2>
                        <p>
                            Baixe o <strong>app próprio da UEB</strong> e tenha sua carteira de estudante
                            sempre à mão — no celular, pronta pra apresentar quando precisar.
                        </p>
                        <div className="lp-tele-apps">
                            <AppleStoreButton />
                            <GooglePlayButton />
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- NÚMEROS ---------------- */}
            <section className="lp-wrap">
                <div className="lp-numeros" data-reveal>
                    {NUMEROS.map((n) => (
                        <div className="lp-numero" key={n.label}>
                            <strong>
                                {n.alvo != null
                                    ? <Contador alvo={n.alvo} prefixo={n.prefixo} sufixo={n.sufixo} />
                                    : n.texto}
                            </strong>
                            <span>{n.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------------- COMO FUNCIONA ---------------- */}
            <section className="lp-secao" id="como-funciona">
                <div className="lp-wrap">
                    <header className="lp-secao-head" data-reveal>
                        <span className="lp-tag">Passo a passo</span>
                        <h2>Da conta criada à carteira na mão</h2>
                        <p>Quatro etapas, todas pelo celular. Sem cartório, sem fila, sem papelada.</p>
                    </header>

                    <ol className="lp-passos">
                        {PASSOS.map((p, i) => (
                            <li className="lp-passo" key={p.titulo} data-reveal style={{ '--d': `${i * 70}ms` }}>
                                <div className="lp-passo-num">{String(i + 1).padStart(2, '0')}</div>
                                <div className="lp-passo-icon">{p.icon}</div>
                                <h3>{p.titulo}</h3>
                                <p>{p.texto}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ---------------- BENEFÍCIOS ---------------- */}
            <section className="lp-secao" id="beneficios">
                <div className="lp-wrap">
                    <header className="lp-secao-head" data-reveal>
                        <span className="lp-tag">Benefícios</span>
                        <h2>Mais que meia-entrada</h2>
                        <p>O que você leva junto com a sua identificação estudantil da UEB.</p>
                    </header>

                    <div className="lp-benef-grid">
                        {BENEFICIOS.map((b, i) => (
                            <article className="lp-benef" key={b.titulo} data-reveal style={{ '--d': `${(i % 3) * 70}ms` }}>
                                <div className="lp-benef-icon">{b.icon}</div>
                                <h3>{b.titulo}</h3>
                                <p>{b.texto}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- FAIXA DE IMAGEM (banner) ---------------- */}
            <section className="lp-wrap">
                <div className="lp-banner" data-reveal>
                    <Figura
                        src={IMAGENS.banner}
                        alt="Sala de cinema com poltronas vermelhas e a tela acesa"
                        medida="1920 × 720 px"
                        proporcao="21 / 9"
                        className="lp-banner-img"
                    />
                    <div className="lp-banner-texto">
                        <h2>Meia-entrada na sessão que você quiser</h2>
                        <p>
                            Apresentou a carteira na bilheteria, pagou metade. A mesma cena vale em
                            show, teatro e jogo — em todo o Brasil, o ano inteiro.
                        </p>
                        <button type="button" className="pbtn pbtn--lg lp-cta-btn" onClick={irParaPortal}>
                            {ctaPrincipal} <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </section>

            {/* ---------------- VALIDAÇÃO ---------------- */}
            <section className="lp-secao" id="validacao">
                <div className="lp-wrap lp-valida">
                    <div className="lp-valida-visual" data-reveal>
                        <div className="lp-qrbox">
                            <QrCode size={92} />
                            <span>Aponte a câmera</span>
                        </div>
                        <div className="lp-selos">
                            <img src={logoIcp} alt="ICP-Brasil" />
                            <img src={logoIti} alt="ITI — Instituto Nacional de Tecnologia da Informação" />
                        </div>
                    </div>

                    <div className="lp-valida-texto" data-reveal>
                        <span className="lp-tag">Autenticidade</span>
                        <h2>Qualquer pessoa pode conferir que ela é <span className="lp-hl">de verdade</span></h2>
                        <p>
                            Cada carteira emitida carrega um código de uso e um QR Code que abrem a página
                            pública de validação. Além disso, o documento em PDF é assinado com o certificado
                            digital ICP-Brasil da UEB — o mesmo padrão usado em documentos oficiais.
                        </p>

                        <ul className="lp-valida-lista">
                            <li><QrCode size={18} /> QR Code que mostra os dados acadêmicos na hora</li>
                            <li><FileCheck2 size={18} /> PDF com assinatura digital verificável</li>
                            <li><BadgeCheck size={18} /> Conferência no validador oficial do gov.br</li>
                        </ul>

                        <a className="pbtn pbtn--soft pbtn--lg" href={GOV_VALIDAR_URL} target="_blank" rel="noopener noreferrer">
                            Validar no site do governo <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </section>

            {/* ---------------- DÚVIDAS ---------------- */}
            <section className="lp-secao lp-secao--alt" id="duvidas">
                <div className="lp-wrap lp-faq-wrap">
                    <header className="lp-secao-head lp-secao-head--left" data-reveal>
                        <span className="lp-tag">Dúvidas frequentes</span>
                        <h2>Ainda com alguma pergunta?</h2>
                        <p>Se a sua não estiver aqui, é só chamar a gente no WhatsApp — respondemos rápido.</p>
                        <button type="button" className="pbtn pbtn--ghost" onClick={abrirWhatsappSuporte}>
                            <Send size={16} /> Falar com o suporte
                        </button>
                    </header>

                    <div className="lp-faq" data-reveal>
                        {DUVIDAS.map((d, i) => {
                            const aberta = faqAberta === i;
                            return (
                                <div className={`lp-faq-item ${aberta ? 'is-open' : ''}`} key={d.pergunta}>
                                    <button
                                        type="button"
                                        className="lp-faq-btn"
                                        aria-expanded={aberta}
                                        onClick={() => setFaqAberta(aberta ? -1 : i)}
                                    >
                                        <span>{d.pergunta}</span>
                                        <span className="lp-faq-sinal" aria-hidden="true" />
                                    </button>
                                    {/* corpo sempre montado: a altura anima via grid-template-rows */}
                                    <div className="lp-faq-corpo">
                                        <div><p>{d.resposta}</p></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ---------------- CTA FINAL ---------------- */}
            <section className="lp-wrap">
                <div className="lp-cta" data-reveal>
                    <div className="lp-cta-texto">
                        <h2>Comece agora a sua carteira {ANO}</h2>
                        <p>Criar a conta é grátis. Você só paga quando finalizar o pedido da carteira.</p>
                        <div className="lp-cta-botoes">
                            <button type="button" className="pbtn pbtn--lg lp-cta-btn" onClick={irParaPortal}>
                                {ctaPrincipal} <ArrowRight size={18} />
                            </button>
                            {!logado && (
                                <Link className="pbtn pbtn--lg lp-cta-btn-ghost" to="/login">Já tenho conta</Link>
                            )}
                        </div>
                    </div>

                    {/* A foto é um recorte cortado na cintura: ela fica ANCORADA
                        no rodapé da faixa, com o corte exatamente na borda de
                        baixo. Assim a faixa "esconde" o corte — parece que a
                        estudante está saindo de dentro dela. */}
                    <div className="lp-cta-figura" aria-hidden="true">
                        <span className="lp-cta-arco" />
                        <img src={fotoEstudante} alt="" />
                    </div>
                </div>
            </section>

            {/* ---------------- RODAPÉ ---------------- */}
            <footer className="lp-footer">
                <div className="lp-wrap lp-footer-grid">
                    <div className="lp-footer-marca">
                        <img src={logoUeb} alt="UEB" />
                        <p>
                            UEB — Carteirinha de Estudante. Identificação estudantil válida em todo o
                            território nacional, em conformidade com a Lei 12.933/13.
                        </p>
                    </div>

                    <div className="lp-footer-col">
                        <span className="lp-footer-titulo">Navegação</span>
                        <a href="#como-funciona">Como funciona</a>
                        <a href="#beneficios">Benefícios</a>
                        <a href="#validacao">Validação</a>
                        <a href="#duvidas">Dúvidas</a>
                    </div>

                    <div className="lp-footer-col">
                        <span className="lp-footer-titulo">Sua conta</span>
                        <Link to="/login">Entrar</Link>
                        <Link to="/registrar">Criar conta</Link>
                        <Link to="/recuperar-acesso">Esqueci minha senha</Link>
                    </div>

                    <div className="lp-footer-col">
                        <span className="lp-footer-titulo">Suporte</span>
                        <button type="button" onClick={abrirWhatsappSuporte}>WhatsApp</button>
                        <a href={GOV_VALIDAR_URL} target="_blank" rel="noopener noreferrer">Validar documento</a>
                    </div>
                </div>

                <div className="lp-wrap lp-footer-base">
                    <span>© {ANO} UEB — Todos os direitos reservados.</span>
                    <span className="lp-footer-selos">
                        <img src={logoIcp} alt="ICP-Brasil" />
                        <img src={logoIti} alt="ITI" />
                    </span>
                </div>
            </footer>

            {/* voltar ao topo */}
            <button
                type="button"
                className={`lp-topo ${aoTopo ? 'is-visivel' : ''}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="Voltar ao topo"
                tabIndex={aoTopo ? 0 : -1}
            >
                <ArrowUp size={20} />
            </button>
        </div>
    );
}
